import { c as _c } from "react/compiler-runtime";
import { cva } from 'class-variance-authority';
import type { CSSProperties, Ref } from 'react';
import type { ChatModelOption } from '@/types/chat';
import { ChatInput } from '../ChatInput';
import type { ChatLayout, QueuedMessage } from './types';
interface ComposerProperties {
  bottomBarInsetStyle: CSSProperties;
  containerRef: (node: HTMLDivElement | null) => void;
  disabled?: boolean | undefined;
  inputPlaceholder?: string | undefined;
  inputRef?: Ref<HTMLTextAreaElement> | null | undefined;
  isLoading: boolean;
  isModelOptionsLoading: boolean;
  keyboardInset: number;
  layout: ChatLayout;
  modelOptions: ChatModelOption[];
  modelOptionsError: string | null | undefined;
  modelPickerEnabled?: boolean | undefined;
  onChange: (value: string) => void;
  onModelChange: (modelId: string) => void;
  onRemoveQueuedMessage?: ((id: string) => void) | undefined;
  onStartFresh?: (() => void) | undefined;
  onStop?: (() => void) | undefined;
  onSubmit: (text: string) => void;
  queuedMessages: QueuedMessage[];
  selectedModelId: string | null;
  value: string;
}
const outerContainerVariants = cva('pointer-events-none inset-x-0 bottom-0 z-layer-overlay', {
  variants: {
    layout: {
      sidebar: 'absolute',
      floating: 'fixed flex justify-center px-4 pb-4 sm:px-6 sm:pb-6'
    }
  },
  defaultVariants: {
    layout: 'floating'
  }
});
const innerContainerVariants = cva('pointer-events-auto w-full', {
  variants: {
    layout: {
      sidebar: 'pb-1',
      floating: 'max-w-4xl'
    }
  },
  defaultVariants: {
    layout: 'floating'
  }
});
export function Composer(t0) {
  const $ = _c(36);
  const {
    layout,
    containerRef,
    bottomBarInsetStyle,
    keyboardInset,
    value,
    onChange,
    onSubmit,
    disabled,
    onStop,
    isLoading,
    modelOptions,
    selectedModelId,
    onModelChange,
    isModelOptionsLoading,
    modelOptionsError,
    onStartFresh,
    queuedMessages,
    onRemoveQueuedMessage,
    inputPlaceholder,
    modelPickerEnabled: t1,
    inputRef: t2
  } = t0;
  const modelPickerEnabled = t1 === undefined ? true : t1;
  const inputRef = t2 === undefined ? null : t2;
  const isSidebar = layout === "sidebar";
  let t3;
  if ($[0] !== keyboardInset) {
    t3 = Math.round(keyboardInset);
    $[0] = keyboardInset;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const keyboardInsetOffsetPx = `${Math.max(0, t3)}px`;
  let t4;
  if ($[2] !== isSidebar || $[3] !== keyboardInsetOffsetPx) {
    t4 = isSidebar ? {
      paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${keyboardInsetOffsetPx} + 0.75rem)`,
      paddingLeft: "env(safe-area-inset-left, 0px)",
      paddingRight: "env(safe-area-inset-right, 0px)"
    } : undefined;
    $[2] = isSidebar;
    $[3] = keyboardInsetOffsetPx;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const sidebarInsetStyle = t4;
  let t5;
  if ($[5] !== layout) {
    t5 = outerContainerVariants({
      layout
    });
    $[5] = layout;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = isSidebar ? sidebarInsetStyle : bottomBarInsetStyle;
  let t7;
  if ($[7] !== layout) {
    t7 = innerContainerVariants({
      layout
    });
    $[7] = layout;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = Boolean(disabled);
  let t9;
  if ($[9] !== inputPlaceholder || $[10] !== inputRef || $[11] !== isLoading || $[12] !== isModelOptionsLoading || $[13] !== layout || $[14] !== modelOptions || $[15] !== modelOptionsError || $[16] !== modelPickerEnabled || $[17] !== onChange || $[18] !== onModelChange || $[19] !== onRemoveQueuedMessage || $[20] !== onStartFresh || $[21] !== onStop || $[22] !== onSubmit || $[23] !== queuedMessages || $[24] !== selectedModelId || $[25] !== t8 || $[26] !== value) {
    t9 = <ChatInput inputRef={inputRef} value={value} onChange={onChange} onSubmit={onSubmit} disabled={t8} onStop={onStop} isLoading={isLoading} modelOptions={modelOptions} selectedModelId={selectedModelId} onModelChange={onModelChange} isModelOptionsLoading={isModelOptionsLoading} modelOptionsError={modelOptionsError} onStartFresh={onStartFresh} queuedMessages={queuedMessages} layout={layout} onRemoveQueuedMessage={onRemoveQueuedMessage} placeholder={inputPlaceholder} modelPickerEnabled={modelPickerEnabled} />;
    $[9] = inputPlaceholder;
    $[10] = inputRef;
    $[11] = isLoading;
    $[12] = isModelOptionsLoading;
    $[13] = layout;
    $[14] = modelOptions;
    $[15] = modelOptionsError;
    $[16] = modelPickerEnabled;
    $[17] = onChange;
    $[18] = onModelChange;
    $[19] = onRemoveQueuedMessage;
    $[20] = onStartFresh;
    $[21] = onStop;
    $[22] = onSubmit;
    $[23] = queuedMessages;
    $[24] = selectedModelId;
    $[25] = t8;
    $[26] = value;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== containerRef || $[29] !== t7 || $[30] !== t9) {
    t10 = <div ref={containerRef} className={t7}>{t9}</div>;
    $[28] = containerRef;
    $[29] = t7;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== t10 || $[33] !== t5 || $[34] !== t6) {
    t11 = <div className={t5} style={t6}>{t10}</div>;
    $[32] = t10;
    $[33] = t5;
    $[34] = t6;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
}